import { useEffect, useRef, useState } from 'react'
import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { useLocation } from 'react-router-dom'
import { PROFILE_DOCUMENTS_TAB, selectMissingDocuments } from '../../../../shared/missingDocuments'
import { connect } from 'react-redux'
import { logIn } from '../../../../redux/ducks/user'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import MiniHeader from '../../../../UI/MiniHeader'
import TabBar from '../../../../UI/TabBar'
import Contact from './components/Contact'
import Security from './components/Security'
import BasicInfo from './components/BasicInfo'
import WorkArea from './components/WorkArea'
import JuniorInfo from './components/JuniorInfo'
import ProfileHeader from './components/ProfileHeader'
import ParentalConf from './components/ParentalConfirmation'
import DocumentsNotice from './components/DocumentsNotice'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import styles from './style.module.scss'

/*
 * Sieben Abschnitte in vier Reitern.
 *
 * Vorher stand alles untereinander auf einer Bahn — Foto, Basisinformation,
 * Wohnadresse, Kontakt, Elternbestätigung, Arbeitsbereich und Passwort. Die Aufteilung folgt dem, wozu etwas gebraucht wird: Wer bin ich
 * (persönliche Daten), was muss ich abgeben (Nachweise), was mache ich in der
 * Firma (Funktion), wie melde ich mich an (Passwort).
 */
const TAB_KEYS = {
	PERSONAL: 'personal',
	// Dasselbe Wort steht in der Adresse (`/profile?tab=documents`), mit der das
	// Warndreieck in der Seitenleiste hierher führt.
	DOCUMENTS: PROFILE_DOCUMENTS_TAB,
	WORK_AREA: 'workArea',
	SECURITY: 'security'
}

function Profile(props) {
	const { t } = useTranslation()
	const location = useLocation()
	const tabFromUrl = () => {
		const wanted = new URLSearchParams(location.search).get('tab')
		return Object.values(TAB_KEYS).includes(wanted) ? wanted : TAB_KEYS.PERSONAL
	}
	const [tab, setTab] = useState(tabFromUrl)

	// Ein Klick auf das Dreieck in der Seitenleiste, während das Profil schon
	// offen ist, ändert nur die Adresse — der Reiter muss trotzdem wechseln.
	useEffect(() => {
		if (new URLSearchParams(location.search).get('tab')) setTab(tabFromUrl())
		/* eslint-disable-next-line */
	}, [location.search, location.key])

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	useEffect(() => {
		http.get(TApi.COMMON_ME, {
			disableToast: true
		}).then(({ data }) => {
			props.logIn({
				role: data.role,
				profile: data
			})
		})
		/* eslint-disable-next-line */
	}, [])

	/*
	 * Ampel an den Reitern: Die orangen Warnbilder an Elternbestätigung und
	 * Arbeitsbereich stünden sonst hinter einem zugeklappten
	 * Reiter — sichtbar erst, wenn man zufällig hinsieht. Der Punkt an der
	 * Reiterleiste holt sie nach vorne.
	 */
	const tabs = [
		{ key: TAB_KEYS.PERSONAL, label: 'personalData' },
		{ key: TAB_KEYS.DOCUMENTS, label: 'documents', done: !props.missingDocuments.length },
		{ key: TAB_KEYS.WORK_AREA, label: 'Functional and working area', done: props.isWorkAreaDone },
		// Das Passwort ändert nur, wem es gehört — eine Lehrkraft in der
		// Schüler:innenansicht sieht sonst ein Formular ohne Speichern-Knopf.
		...(props.isTeacherStudentView ? [] : [{ key: TAB_KEYS.SECURITY, label: 'Change Password' }])
	]

	return (
		<div className={styles.wrapper}>
			<MiniHeader title={t('My Profile')} bg="#f8faff" />

			<ProfileHeader isTeacherStudentView={props.isTeacherStudentView} />

			<TabBar className={styles.tabs} tabs={tabs} active={tab} onChange={setTab} tabsRef={tabsRef} />

			<div className={styles.panel}>
				{tab === TAB_KEYS.PERSONAL && (
					<>
						<BasicInfo isTeacherStudentView={props.isTeacherStudentView} />
						<JuniorInfo isTeacherStudentView={props.isTeacherStudentView} />
						<Contact isTeacherStudentView={props.isTeacherStudentView} />
					</>
				)}

				{tab === TAB_KEYS.DOCUMENTS && (
					<>
						<DocumentsNotice
							missing={props.missingDocuments}
							isTeacherStudentView={props.isTeacherStudentView}
						/>
						<ParentalConf isTeacherStudentView={props.isTeacherStudentView} />
					</>
				)}

				{tab === TAB_KEYS.WORK_AREA && <WorkArea isTeacherStudentView={props.isTeacherStudentView} />}

				{tab === TAB_KEYS.SECURITY && !props.isTeacherStudentView && (
					<Security isTeacherStudentView={props.isTeacherStudentView} />
				)}
			</div>
		</div>
	)
}

Profile.propTypes = {
	isTeacherStudentView: PropTypes.bool.isRequired,
	missingDocuments: PropTypes.array.isRequired,
	isWorkAreaDone: PropTypes.bool.isRequired
}

const mapDispatchToProps = dispatch => ({
	logIn: data => dispatch(logIn(data))
})

const mapStateToProps = state => {
	const profile = state.user.profile

	return {
		isTeacherStudentView: state.user.isTeacherStudentView,
		// Die Elternbestätigung. Dieselbe Liste speist das Warndreieck in der Seitenleiste.
		missingDocuments: selectMissingDocuments(state),
		isWorkAreaDone: !!profile.isCEO || !!profile.primaryWorkArea
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(Profile)
